"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { ClientService } from "@/apis/client/ClientService"
import { MaterialService } from "@/apis/material/MaterialService"
import { ProjectService } from "@/apis/project/ProjectService"
import {
    type Client,
    type Material,
    type ProjectMaterialRequest,
    type ProjectRequest,
    type ProjectResponse,
    Status,
    Type,
    type User,
    type UserResponse,
} from "@/utils/types"
import { Input } from "@/components/ui/input";
import { UserService } from "@/apis/profile/UserService.tsx"
import Select, { type MultiValue, type SingleValue } from "react-select"
import {
    Select as Select2,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar.tsx"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button.tsx"
import {BarChart, PlusCircle, Trash2} from "lucide-react"
import { ProgressUpdateDialog } from "@/components/ui/progress-update-dialog"
import {Label} from "@radix-ui/react-label";

const EditProjectPage = () => {
    const { projectId } = useParams<{ projectId: string }>() // Get projectId from URL
    const [currentUser, setCurrentUser] = useState<User>()
    const [clients, setClients] = useState<Client[]>([])
    const [materials, setMaterials] = useState<Material[]>([])
    const [users, setUsers] = useState<UserResponse[]>([]) // State to hold users
    const [selectedUsers, setSelectedUsers] = useState<UserResponse[]>([]) // Selected users for the project
    const [selectedClient, setSelectedClient] = useState<Client>()
    const [projectMaterials, setProjectMaterials] = useState<ProjectMaterialRequest[]>([])
    const [selectedProject, setSelectedProject] = useState<ProjectResponse | null>(null)
    const [progressDialogOpen, setProgressDialogOpen] = useState(false)
    const [projectData, setProjectData] = useState<ProjectRequest>({
        name: "",
        description: "",
        clientId: 0,
        address: "",
        materials: [],
        price: 0,
        advance: 0,
        invoice: false,
        status: Status.POTENTIAL,
        startDate: "",
        limitDate: "",
        points: 0,
        archived: false
    })
    const navigate = useNavigate()


    useEffect(() => {
        const fetchData = async () => {
            try {
                const materialsData = await MaterialService.getMaterials();
                setMaterials(materialsData);

                const projectData = await ProjectService.getProject(Number(projectId));
                // Aici presupunem că `projectData.materials` are [{ materialId, quantity }]
                setProjectMaterials(projectData.materials || []);
            } catch (error) {
                console.error("Eroare la încărcarea datelor:", error);
            }
        };
        loadUserInfo()
        loadClients()
        loadMaterials()
        loadUsers()
        loadProject()

        fetchData();
    }, [projectId]);

    const loadUserInfo = async () => {
        await UserService.getCurrentUser().then((user) => {
            setCurrentUser(user)
        })
    }

    const loadClients = async () => {
        const clientsData = await ClientService.getClients()
        setClients(clientsData)
    }

    const loadMaterials = async () => {
        const materialsData = await MaterialService.getMaterials()
        setMaterials(materialsData)
    }

    const loadUsers = async () => {
        const usersData = await UserService.getUsersByType(Type.ANGAJAT) // Load available users
        setUsers(usersData)
    }

    const loadProject = async () => {
        if (projectId) {
            const project = await ProjectService.getProject(Number(projectId))
            setProjectData({
                name: project.name,
                description: project.description,
                clientId: project.clientId,
                address: project.address,
                materials: project.materials,
                price: project.price,
                advance: project.advance,
                invoice: project.invoice,
                status: project.status,
                startDate: project.startDate,
                limitDate: project.limitDate,
                points: project.points,
                archived: project.archived
            })

            // Set the selected project for progress dialog
            setSelectedProject(project)

            // Avoid multiple re-renders for client and users
            const client = await ClientService.getClientById(project.clientId)
            if (client.id !== selectedClient?.id) {
                setSelectedClient(client)
            }

            const projectUsers = await UserService.getUsersByProjectId(project.id)
            setSelectedUsers(projectUsers)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setProjectData((prevState) => ({ ...prevState, [name]: value }))
    }

    const handleMaterialChange = (index: number, field: string, value: number) => {
        const newMaterials = [...projectMaterials]
        newMaterials[index] = { ...newMaterials[index], [field]: value }
        setProjectMaterials(newMaterials)
    }

    const handleUserChange = (selectedOptions: MultiValue<UserResponse>) => {
        setSelectedUsers(selectedOptions as UserResponse[]) // Cast it to UserResponse[] if needed
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (selectedClient != undefined) {
            const projectToSubmit = { ...projectData, clientId: selectedClient.id, materials: projectMaterials }
            await ProjectService.updateProject(Number(projectId), projectToSubmit)

            // Fetch the current users assigned to the project
            const currentProjectUsers = await UserService.getUsersByProjectId(Number(projectId))

            // Find users to add (those in selectedUsers but not in currentProjectUsers)
            const usersToAdd = selectedUsers.filter(
                (user) => !currentProjectUsers.some((projectUser: { id: number }) => projectUser.id === Number(user.id)),
            )

            // Find users to remove (those in currentProjectUsers but not in selectedUsers)
            const usersToRemove = currentProjectUsers.filter(
                (projectUser: UserResponse) => !selectedUsers.some((user) => user.id === projectUser.id),
            )

            // Add new users to the project
            await Promise.all(usersToAdd.map((user) => ProjectService.addUserToProject(Number(projectId), Number(user.id))))

            // Remove users that are no longer selected
            await Promise.all(
                usersToRemove.map((user: User) => ProjectService.removeUserFromProject(Number(projectId), Number(user.id))),
            )

            alert("Proiect actualizat cu succes")
            navigate("/projects")
        } else {
            alert("Selectează un client")
        }
    }

    const handleOpenProgressDialog = (e: React.MouseEvent) => {
        // Prevent the form from submitting when clicking the progress button
        e.preventDefault()

        if (selectedProject) {
            setProgressDialogOpen(true)
        } else {
            alert("Nu se poate actualiza progresul. Proiectul nu este încărcat complet.")
        }
    }

    const handleCloseProgressDialog = () => {
        setProgressDialogOpen(false)
    }

    const handleProgressUpdate = async () => {
        await loadProject()
    }

    const addMaterial = () => {
        setProjectMaterials((prevMaterials) => [...prevMaterials, { materialId: 0, quantity: 0 }])
    }

    const removeMaterial = (index: number) => {
        setProjectMaterials((prevMaterials) =>
            prevMaterials.filter((_, i) => i !== index),
        );
    };


    const getStatusOptions = () => {
        return Object.keys(Status).map((key) => ({
            value: Status[key as keyof typeof Status],
            label: Status[key as keyof typeof Status],
        }))
    }

    if (currentUser && currentUser.username && currentUser.password && currentUser.id) {
        return (
          <div className="min-h-screen bg-white">
            <Navbar />
            <div className="container mx-auto p-4">
              <h1 className="mb-4 text-2xl font-bold">Editează Proiect</h1>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name">Nume Proiect:</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={projectData.name}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description">Descriere:</label>
                  <textarea
                    id="description"
                    name="description"
                    value={projectData.description}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="client">Client:</label>
                  <Select
                    options={clients}
                    getOptionLabel={(client) => client.name}
                    getOptionValue={(client) => client.id.toString()}
                    value={selectedClient}
                    onChange={(newValue: SingleValue<Client> | null) => {
                      setSelectedClient(newValue ?? undefined);
                    }}
                    placeholder="Caută și selectează un client..."
                    isSearchable
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="address">Adresă:</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={projectData.address}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <Label>Materiale</Label>

                  {projectMaterials.map((material, index) => (
                    <div
                      key={index}
                      className="mb-2 flex items-center space-x-2"
                    >
                      <Select2
                        value={material.materialId?.toString() ?? ""}
                        onValueChange={(value) =>
                          handleMaterialChange(
                            index,
                            "materialId",
                            parseInt(value),
                          )
                        }
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Selectează un material" />
                        </SelectTrigger>
                        <SelectContent>
                          {materials.map((mat) => (
                            <SelectItem key={mat.id} value={mat.id.toString()}>
                              {mat.name} (Coef: {mat.coefficient})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select2>

                      <Input
                        type="number"
                        placeholder="Cantitate"
                        value={material.quantity ?? ""}
                        onChange={(e) =>
                          handleMaterialChange(
                            index,
                            "quantity",
                            parseFloat(e.target.value),
                          )
                        }
                        className="w-[100px]"
                      />

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeMaterial(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addMaterial}
                    className="mt-2"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Adaugă Material
                  </Button>
                </div>

                <div>
                  <label>Utilizatori:</label>
                  <Select
                    options={users}
                    getOptionLabel={(user) => user.name}
                    getOptionValue={(user) => user.id.toString()}
                    value={selectedUsers}
                    onChange={handleUserChange}
                    placeholder="Caută și selectează utilizatori..."
                    isSearchable
                    isMulti
                    className="border-gray-300 w-full border p-2"
                  />
                </div>

                <div>
                  <label htmlFor="price">Preț:</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={projectData.price}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="advance">Avans:</label>
                  <input
                    type="number"
                    id="advance"
                    name="advance"
                    value={projectData.advance}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="invoice">Factură:</label>
                  <input
                    type="checkbox"
                    id="invoice"
                    name="invoice"
                    checked={projectData.invoice}
                    onChange={() =>
                      setProjectData((prevState) => ({
                        ...prevState,
                        invoice: !prevState.invoice,
                      }))
                    }
                  />
                </div>

                <div>
                  <label htmlFor="status">Status:</label>
                  <Select
                    options={getStatusOptions()}
                    value={{
                      value: projectData.status,
                      label: projectData.status,
                    }}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        // Check for null
                        setProjectData((prevState) => ({
                          ...prevState,
                          status: selectedOption.value,
                        }));
                      }
                    }}
                    placeholder="Selectează un status..."
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="startDate">Data de inceput:</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={projectData.startDate}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="limitDate">Data limita:</label>
                  <input
                    type="date"
                    id="limitDate"
                    name="limitDate"
                    value={projectData.limitDate}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="points">Puncte:</label>
                  <input
                    type="number"
                    id="points"
                    name="points"
                    value={projectData.points}
                    onChange={handleInputChange}
                    className="border-gray-300 w-full border p-2"
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="submit"
                    className="rounded bg-grey px-4 py-2 text-white"
                  >
                    Salvează Modificările
                  </button>

                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleOpenProgressDialog}
                    type="button" // Explicitly set type to button to prevent form submission
                  >
                    <BarChart className="mr-2 h-4 w-4" />
                    Actualizare Progres
                  </Button>
                </div>
              </form>

              {selectedProject && (
                <ProgressUpdateDialog
                  project={selectedProject}
                  isOpen={progressDialogOpen}
                  onClose={handleCloseProgressDialog}
                  onProgressUpdate={handleProgressUpdate}
                />
              )}
            </div>
          </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <Navbar/>
        </div>
    )
}

export default EditProjectPage

